// © Microsoft Corporation. All rights reserved.

import { invoke, listen, unlisten } from "./SapphireCore";

import { SapphireBridgeCallBackFunction } from "./SapphireTypes";

// Method: navigate
// Description: Navigate to another page or feature.
function navigate(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("navigate", data, callback);
}

/**
 * Async Navigate to another page or feature.
 * @param data parameters for method "navigate".
 */
function navigateAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        navigate(data, (result) => {
            resolve(result);
        });
    });
}

/**
 * Sync Navigate to another page or feature.
 * @param data parameters for method "navigate".
 * @param callback callback with response returned from sapphire.
 */
function navigateSync(data: Object, callback: any) {
    navigate(data, callback);
}

// Method: requestBrowser
// Description: Navigate to in-app browser.
function requestBrowser(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("requestBrowser", data, callback);
}

/**
 * Navigate to in-app browser.
 * @param data parameters for method "requestBrowser".
 */
function requestBrowserAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        requestBrowser(data, (result) => {
            resolve(result);
        });
    });
}

function getHttp(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("getHttp", data, callback);
}

/**
 * Send a HTTP Get request.
 * @param data parameters for method "getHttp".
 */
function getHttpAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        getHttp(data, (result) => {
            resolve(result);
        });
    });
}

function postHttp(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("postHttp", data, callback);
}

/**
 * Send a HTTP Post request.
 * @param data parameters for method "postHttp".
 */
function postHttpAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        postHttp(data, (result) => {
            resolve(result);
        });
    });
}

function putHttp(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("putHttp", data, callback);
}

/**
 * Send a HTTP Put request.
 * @param data parameters for method "putHttp".
 */
function putHttpAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        putHttp(data, (result) => {
            resolve(result);
        });
    });
}

function deleteHttp(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("deleteHttp", data, callback);
}

/**
 * Send a HTTP Delete request.
 * @param data parameters for method "deleteHttp".
 */
function deleteHttpAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        deleteHttp(data, (result) => {
            resolve(result);
        });
    });
}

function logEvent(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("logEvent", data, callback);
}

/**
 * Log an event to telemetry manager.
 * @param data parameters for method "logEvent".
 */
function logEventAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        logEvent(data, (result) => {
            resolve(result);
        });
    });
}

function logError(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("logError", data, callback);
}

/**
 * Log an error to telemetry manager.
 * @param data parameters for method "logError".
 */
function logErrorAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        logError(data, (result) => {
            resolve(result);
        });
    });
}

function getDeviceInfo(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("getDeviceInfo", data, callback);
}

/**
 * Request for device information.
 * @param data parameters for method "getDeviceInfo".
 */
function getDeviceInfoAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        getDeviceInfo(data, (result) => {
            resolve(result);
        });
    });
}

function getBatteryInfo(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("getBatteryInfo", data, callback);
}

/**
 * Get battery information.
 * @param data parameters for method "getBatteryInfo".
 */
function getBatteryInfoAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        getBatteryInfo(data, (result) => {
            resolve(result);
        });
    });
}

function getLocationInfo(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("getLocationInfo", data, callback);
}

/**
 * Request for system location information.
 * @param data parameters for method "getLocationInfo".
 */
function getLocationInfoAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        getLocationInfo(data, (result) => {
            resolve(result);
        });
    });
}

function getNetworkInfo(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("getNetworkInfo", data, callback);
}

/**
 * Get network information.
 * @param data parameters for method "getNetworkInfo".
 */
function getNetworkInfoAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        getNetworkInfo(data, (result) => {
            resolve(result);
        });
    });
}

function getUserInfo(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("getUserInfo", data, callback);
}

/**
 * Request for user sign in related information.
 * @param data parameters for method "getUserInfo".
 */
function getUserInfoAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        getUserInfo(data, (result) => {
            resolve(result);
        });
    });
}

function getAppList(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("getAppList", data, callback);
}

/**
 * Request for mini app list.
 * @param data parameters for method "getAppList".
 */
function getAppListAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        getAppList(data, (result) => {
            resolve(result);
        });
    });
}

function requestDialog(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("requestDialog", data, callback);
}

/**
 * Show a dialog.
 * @param data parameters for method "requestDialog".
 */
function requestDialogAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        requestDialog(data, (result) => {
            resolve(result);
        });
    });
}

function requestPermission(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("requestPermission", data, callback);
}

/**
 * Request permissions from super app, such as camera, microphone and others.
 * @param data parameters for method "requestPermission".
 */
function requestPermissionAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        requestPermission(data, (result) => {
            resolve(result);
        });
    });
}

function requestToast(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("requestToast", data, callback);
}

/**
 * Request to display a notice or toast on screen.
 * @param data parameters for method "requestToast".
 */
function requestToastAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        requestToast(data, (result) => {
            resolve(result);
        });
    });
}

function requestSnackBar(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("requestSnackBar", data, callback);
}

/**
 * Show a snack bar with style.
 * @param data parameters for method "requestSnackBar".
 */
function requestSnackBarAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        requestSnackBar(data, (result) => {
            resolve(result);
        });
    });
}

function requestVibration(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("requestVibration", data, callback);
}

/**
 * Request device to viberate.
 * @param data parameters for method "requestVibration".
 */
function requestVibrationAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        requestVibration(data, (result) => {
            resolve(result);
        });
    });
}

function requestRestart(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("requestRestart", data, callback);
}

/**
 * Restart super app.
 * @param data parameters for method "requestRestart".
 */
function requestRestartAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        requestRestart(data, (result) => {
            resolve(result);
        });
    });
}

function requestOrientation(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("requestOrientation", data, callback);
}

/**
 * Change device"s orientation.
 * @param data parameters for method "requestOrientation".
 */
function requestOrientationAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        requestOrientation(data, (result) => {
            resolve(result);
        });
    });
}

/**
 * Subscribe event handler.
 * @param data parameters for register event handler.
 * @param handler event handler.
 * @returns event handler id.
 */
function subscribe(data: Object, handler: SapphireBridgeCallBackFunction): string {
    return listen(data, handler);
}

/**
 * Unsubscribe event handler.
 * @param handlerId event handler id.
 * @param data parameters for unregister event handler.
 * @param callback callback when unregistered successfully.
 */
function unsubscribe(handlerId: string, data: Object, callback?: SapphireBridgeCallBackFunction) {
    unlisten(handlerId, data, callback);
}

function sendBroadcast(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("sendBroadcast", data, callback);
}

/**
 * Request to send a broadcast message.
 * @param data parameters for method "sendBroadcast".
 */
function sendBroadcastAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        sendBroadcast(data, (result) => {
            resolve(result);
        });
    });
}

function fetchAds(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("fetchAds", data, callback);
}

/**
 * Get ads data from super app.
 * @param data parameters for method "fetchAds".
 */
function fetchAdsAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        fetchAds(data, (result) => {
            resolve(result);
        });
    });
}

function requestCustom(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("requestCustom", data, callback);
}

/**
 * Request to send data to a custom feature.
 * @param data parameters for method "requestCustom".
 */
function requestCustomAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        requestCustom(data, (result) => {
            resolve(result);
        });
    });
}

function loadData(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("loadData", data, callback);
}

/**
 * Load data from internal database.
 * @param data parameters for method "loadData".
 */
function loadDataAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        loadData(data, (result) => {
            resolve(result);
        });
    });
}

function saveData(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("saveData", data, callback);
}

/**
 * Save simple data to internal database.
 * @param data parameters for method "saveData".
 */
function saveDataAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        saveData(data, (result) => {
            resolve(result);
        });
    });
}

function manageData(data: Object, callback: SapphireBridgeCallBackFunction) {
    invoke("requestShare", data, callback);
}

/**
 * Manage internal database.
 * @param data parameters for method "manageData".
 */
function manageDataAsync(data: Object) {
    return new Promise<string>((resolve, reject) => {
        manageData(data, (result) => {
            resolve(result);
        });
    });
}

export {
    /* navigate interfaces */
    navigate,
    navigateAsync,
    navigateSync,
    requestBrowser,
    requestBrowserAsync,
    /* network interfaces */
    getHttp,
    getHttpAsync,
    postHttp,
    postHttpAsync,
    putHttp,
    putHttpAsync,
    deleteHttp,
    deleteHttpAsync,
    /* log interfaces */
    logEvent,
    logEventAsync,
    logError,
    logErrorAsync,
    /* get interfaces */
    getDeviceInfo,
    getDeviceInfoAsync,
    getBatteryInfo,
    getBatteryInfoAsync,
    getLocationInfo,
    getLocationInfoAsync,
    getNetworkInfo,
    getNetworkInfoAsync,
    getUserInfo,
    getUserInfoAsync,
    getAppList,
    getAppListAsync,
    /* request interfaces */
    requestDialog,
    requestDialogAsync,
    requestPermission,
    requestPermissionAsync,
    requestToast,
    requestToastAsync,
    requestSnackBar,
    requestSnackBarAsync,
    requestVibration,
    requestVibrationAsync,
    requestRestart,
    requestRestartAsync,
    requestOrientation,
    requestOrientationAsync,
    /* subscribe interfaces */
    subscribe,
    unsubscribe,
    sendBroadcast,
    sendBroadcastAsync,
    /* ads interfaces */
    fetchAds,
    fetchAdsAsync,
    /* custom interfaces */
    requestCustom,
    requestCustomAsync,
    /* data interfaces */
    loadData,
    loadDataAsync,
    saveData,
    saveDataAsync,
    manageData,
    manageDataAsync
};
